import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form, Button } from "react-bootstrap";
import AccordionComponent from "../../../../../common/Accordion";

import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

import "../../../../../utils/initHighchartsModules";
import {
  AppointmentSummary,
  getAppointmentRORSummary,
  getRORAppointmentAnalyticsView,
} from "../../../../../services/RORDashboardAPI";

const RORAppointmentSummary: React.FC = () => {
  const [appointments, setAppointments] = useState<AppointmentSummary[]>([]);
  const [analyticData, setAnalyticData] = useState<any>(null);

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const result = await getAppointmentRORSummary();
        setAppointments(result);
      } catch (error) {
        console.error("Failed to load appointments summary:", error);
      }
    };

    fetchAppointments();
  }, []);

  const fetchAnalyticSummary = async () => {
    try {
      const data = await getRORAppointmentAnalyticsView();
      setAnalyticData(data);
    } catch (error) {
      console.error("Failed to fetch appointment cases:", error);
    }
  };

  const backgroundMap: { [key: string]: string } = {
    Today: "bg-customBlue",
    "This Week": "bg-customPurple",
    "This Month": "bg-customYellow",
    Future: "bg-customGreen",
    Past: "bg-customGray",
  };

  const iconMap: { [key: string]: JSX.Element } = {
    Today: <span className="material-symbols-outlined">date_range</span>,
    "This Week": (
      <span className="material-symbols-outlined">calendar_view_week</span>
    ),
    "This Month": <span className="material-symbols-outlined">date_range</span>,
    Future: <span className="material-symbols-outlined">schedule</span>,
    Past: <span className="material-symbols-outlined">hourglass_empty</span>,
  };

  const [DashboardAppointmentKey, setDashboardAppointmentKey] = useState<
    string | null
  >("0");

  const handleBodyAccordionSelect = (key: string | null) => {
    // Prevent collapsing if the same key is clicked
    if (DashboardAppointmentKey === key) {
      return; // do nothing
    }
    setDashboardAppointmentKey(key || "0");
  };

  const DashboardAppointmentAccordion = (
    items: { title: JSX.Element; content: JSX.Element }[],
  ) => (
    <AccordionComponent
      items={items.map((item, index) => ({
        ...item,
      }))}
      activeKey={DashboardAppointmentKey}
      onSelect={handleBodyAccordionSelect}
    />
  );
  const [toggle, setToggle] = useState<boolean>(false);

  const onTimePercentage =
    appointments.find((a) => a.appointmentType === "Today")
      ?.appointmentValues_OnTime || 75;

  return (
    <>
      {DashboardAppointmentAccordion([
        {
          title: (
            <div className="d-flex justify-content-between align-items-center w-100 p-0">
              <span className="">Appointments</span>
              <div
                className="d-flex align-items-center"
                onClick={(e) => e.stopPropagation()}
              >
                {toggle ? (
                  // Show bar chart is active, switch to list view
                  <button
                    className="btn btn-sm p-1  appointment-toggle-btn d-flex align-items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      setToggle(false);
                    }}
                    title="List View"
                  >
                    <span className="material-symbols-outlined  fs-6">
                      article
                    </span>
                  </button>
                ) : (
                  // Show list is active, switch to chart view
                  <button
                    className="btn btn-sm p-1 appointment-toggle-btn d-flex align-items-center"
                    onClick={(e) => {
                      e.stopPropagation();
                      setToggle(true);
                      fetchAnalyticSummary();
                    }}
                    title="Chart View"
                  >
                    <span className="material-symbols-outlined  fs-6">
                      monitoring
                    </span>
                  </button>
                )}
              </div>
            </div>
          ),
          content: (
            <div className="bg-white rounded-xl shadow-md">
              {toggle ? (
                <>
                  <div className="text-center">
                    Total Appointment{" "}
                    <span className="total-appointment">
                      {analyticData?.app_Total ?? 0}
                    </span>
                  </div>

                  <HighchartsReact
                    highcharts={Highcharts}
                    options={{
                      chart: {
                        type: "solidgauge",
                        height: "75%",
                      },
                      title: { text: undefined },
                      tooltip: {
                        enabled: true,
                        format: "<b>{series.name}</b>: {point.y}%",
                      },
                      pane: {
                        startAngle: 0,
                        endAngle: 360,
                        background: [
                          {
                            outerRadius: "112%",
                            innerRadius: "100%",
                            backgroundColor: "#bee7ff",
                            borderWidth: 0,
                          },
                          {
                            outerRadius: "87%",
                            innerRadius: "63%",
                            backgroundColor: "#dbe2b6",
                            borderWidth: 0,
                          },
                          {
                            outerRadius: "62%",
                            innerRadius: "38%",
                            backgroundColor: "#fad28a",
                            borderWidth: 0,
                          },
                        ],
                      },
                      yAxis: {
                        min: 0,
                        max: 100,
                        lineWidth: 0,
                        tickPositions: [],
                      },
                      plotOptions: {
                        solidgauge: {
                          dataLabels: {
                            enabled: false,
                          },
                          rounded: true,
                        },
                      },

                      series: [
                        {
                          name: "Scheduled",
                          type: "solidgauge",
                          data: [
                            {
                              color: "#0192d5",
                              radius: "100%",
                              innerRadius: "88%",
                              y: analyticData?.app_Per_Scheduled ?? 0,
                            },
                          ],
                        },
                        {
                          name: "Completed",
                          type: "solidgauge",
                          data: [
                            {
                              color: "#a2b063",
                              radius: "75%",
                              innerRadius: "63%",
                              y: analyticData?.app_Per_Completed ?? 0,
                            },
                          ],
                        },
                        {
                          name: "Cancelled",
                          type: "solidgauge",
                          data: [
                            {
                              color: "#fea500",
                              radius: "50%",
                              innerRadius: "38%",
                              y: analyticData?.app_Per_Cancelled ?? 0,
                            },
                          ],
                        },
                      ],
                    }}
                  />

                  <div className="d-flex flex-row justify-content-evenly">
                    <div className=" me-3 font-small text-center">
                      <span
                        style={{
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#fea500",
                          borderRadius: "50%",
                          display: "inline-block",
                          marginRight: "5px",
                        }}
                      ></span>
                      <span>Cancelled</span>
                      <p className="mb-0">
                        {analyticData?.app_Per_Cancelled ?? 0}%
                      </p>
                    </div>
                    <div className=" me-3 font-small text-center">
                      <span
                        style={{
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#a2b063",
                          borderRadius: "50%",
                          display: "inline-block",
                          marginRight: "5px",
                        }}
                      ></span>

                      <span>Completed</span>
                      <p className="mb-0">
                        {analyticData?.app_Per_Completed ?? 0}%
                      </p>
                    </div>
                    <div className=" font-small text-center">
                      <span
                        style={{
                          width: "10px",
                          height: "10px",
                          backgroundColor: "#0192d5",
                          borderRadius: "50%",
                          display: "inline-block",
                          marginRight: "5px",
                        }}
                      ></span>
                      <span>Scheduled</span>
                      <p className="mb-0">
                        {analyticData?.app_Per_Scheduled ?? 0}%
                      </p>
                    </div>
                  </div>

                  <div className="mt-1">
                    <div className="text-center fw-normal mb-1 border-top">
                      Appointment On Time
                    </div>
                    <div
                      className="position-relative"
                      style={{ height: "25px" }}
                    >
                      <div
                        style={{
                          background: `linear-gradient(
  to right,
  #d6e980 ${analyticData?.app_Per_OnTime_Total ?? 0}%,
  #808080 ${analyticData?.app_Per_OnTime_Total ?? 0}%
)`,
                          height: "100%",
                          borderRadius: "4px",
                        }}
                      />
                      <div
                        className="position-absolute top-0 start-50 translate-middle-x text-dark fw-bold"
                        style={{ lineHeight: "30px", fontSize: "12px" }}
                      >
                        {analyticData?.app_Per_OnTime_Total ?? 0}%
                      </div>
                    </div>
                    <div
                      className="d-flex justify-content-between mt-1 px-1 text-muted "
                      style={{ fontSize: "12px" }}
                    >
                      <span>0</span>
                      <span>20</span>
                      <span>40</span>
                      <span>60</span>
                      <span>80</span>
                      <span>100</span>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="d-flex flex-row  slide-up-onload">
                    <div className="w-54 text-end scheduled fw-small pe-2 font-small">
                      Scheduled
                    </div>
                    <div className="w-24 text-end completed fw-small pe-2 font-small">
                      Completed
                    </div>
                    <div className="w-22 text-end cancelled fw-small pe-2 font-small">
                      Cancelled
                    </div>
                  </div>

                  <div className="w-100">
                    {appointments.map((item, index) => (
                      <div
                        key={index}
                        className={` slide-up-onload p-2 mb-2 ${
                          backgroundMap[item.appointmentType]
                        }`}
                      >
                        <div className="d-flex align-items-center">
                          <div className="me-auto w-35 d-flex align-items-center border-end py-2">
                            <span className="appoint-icon d-flex align-items-center">
                              {iconMap[item.appointmentType] || "📌"}
                            </span>
                            <span className="appoint-text">
                              {item.appointmentType}
                            </span>
                          </div>

                          <span className="text-center scheduled w-18 fw-bolder border-end">
                            {item.appointmentValues_Scheduled
                              .toString()
                              .padStart(2, "0")}
                          </span>
                          <span className="text-center completed w-24 fw-bolder border-end">
                            {item.appointmentValues_Completed
                              .toString()
                              .padStart(2, "0")}
                          </span>
                          <span className="text-center cancelled w-24 fw-bolder">
                            {item.appointmentValues_Cancelled
                              .toString()
                              .padStart(2, "0")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          ),
        },
      ])}
    </>
  );
};

export default RORAppointmentSummary;
